import { motion } from "framer-motion";
import { FileText, Lightbulb, Cog, TrendingUp, BarChart } from "lucide-react";

export function ProcessSection() {
  const processSteps = [
    {
      icon: FileText,
      title: "Brief",
      description: "Understanding project requirements and business objectives"
    },
    {
      icon: Lightbulb,
      title: "Strategy",
      description: "Building optimal marketing strategy and action plan"
    },
    {
      icon: Cog,
      title: "Production",
      description: "Execution and content creation across all channels"
    },
    {
      icon: TrendingUp,
      title: "Optimization",
      description: "Performance monitoring and continuous improvement"
    },
    {
      icon: BarChart,
      title: "Report",
      description: "Results analysis and ROI evaluation"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Process
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-700 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A 5-step process ensuring maximum quality and effectiveness
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Desktop Timeline */}
          <div className="hidden md:block relative">
            {/* Timeline Line */}
            <div className="absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-200 via-orange-300 to-orange-400"></div>
            
            <div className="grid grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {/* Step Number & Icon */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="bg-white border-4 border-orange-200 rounded-full w-16 h-16 flex items-center justify-center mb-4 relative z-10">
                      <step.icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="absolute top-14 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center flex-shrink-0">
                  <step.icon className="w-6 h-6 text-orange-600" />
                </div>
                <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}